import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const CrescentHeights = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName}, ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName}, ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.crescent-heights-calgary:About Paragraph">
          Crescent Heights, situated just north of downtown Calgary,{' '}
          <LinkText href="/rental-listings/alberta">Alberta</LinkText>, is a
          historic inner-city neighborhood celebrated for its panoramic views of
          the city skyline and the Bow River. Established in 1914, it offers a
          blend of early 20th-century character homes and modern infill
          developments. Its elevated position provides residents with some of
          the most stunning vistas in{' '}
          <LinkText href="/rental-listings/city/calgary">Calgary</LinkText>,
          while its proximity to downtown ensures easy access to urban
          amenities. The community’s rich history, diverse architecture, and
          vibrant atmosphere make it a sought-after location for a variety of
          residents.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$1,300 - $1,900',
          bed2: '$1,400 - $2,400',
          bed3: '$2,100 - $3,000'
        }}
        rentReportCity="Alberta"
        rentReportLink="https://liv.rent/blog/rent-reports/alberta/"
        rentReportLinkZh="https://liv.rent/blog/zh/%e7%a7%9f%e9%87%91%e7%bb%9f%e8%ae%a1/%e9%98%bf%e5%b0%94%e4%bc%af%e5%a1%94/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.crescent-heights-calgary:Apartment Hunting Paragraph 1">
            Compared to cities like Vancouver and Richmond, it’s fairly easy to
            find relatively cheap rentals in {{ neighborhoodNameWithCity }} -
            although{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon. To start your search, you can refer to this
            guide on {{ neighborhoodNameWithCity }}
            neighbourhoods.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.crescent-heights-calgary:Apartment Hunting Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long-term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short-term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.crescent-heights-calgary:Apartments for rent Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Crescent Heights, Calgary
              </LinkText>
              , appeal to young professionals and students seeking modern living
              spaces close to downtown. These units often feature contemporary
              designs and are situated near public transit, boutique shops, and
              cafes, offering a vibrant urban lifestyle.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.crescent-heights-calgary:Houses for rent Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Crescent Heights, Calgary
              </LinkText>
              , are ideal for families and individuals desiring more space
              within a historic setting. The neighborhood offers a mix of
              charming heritage homes and modern infills, often with yards and
              close to parks. Living here provides a blend of community feel and
              easy access to downtown amenities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.crescent-heights-calgary:Townhouses for rent Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Crescent Heights, Calgary
              </LinkText>
              , offer a balanced living option for small families and
              professionals. These multi-level homes often come with attached
              garages and private patios, situated near shopping centers,
              schools, and public transit, combining convenience with a sense of
              community.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.crescent-heights-calgary:Basements for rent Paragraph">
              Basements for rent in Crescent Heights, Calgary, provide
              affordable living solutions for students and individuals. These
              suites typically include private entrances and essential
              amenities, located within walking distance to the University of
              Calgary, transit stations, and local shops, offering both comfort
              and accessibility.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.crescent-heights-calgary:Condos for rent Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Crescent Heights, Calgary
              </LinkText>
              , are a popular choice for those who value convenience and a sense
              of community. With amenities such as fitness centers, rooftop
              gardens, and concierge services, condos for rent in Crescent
              Heights Calgary offer a maintenance-free lifestyle. Residents can
              enjoy the vibrant energy of the neighborhood while having all the
              comforts of home.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.crescent-heights-calgary:Rooms for rent Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in Crescent Heights, Calgary
              </LinkText>
              , are a budget-friendly option for students and newcomers. Often
              found in shared houses, these rooms provide access to communal
              areas like kitchens and living rooms. The neighborhood’s friendly
              community and access to public transit make it an attractive
              choice for renters.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.crescent-heights-calgary:Short term rentals Paragraph">
              <LinkText href={links.shortTerm}>
                Short-term rentals in Crescent Heights, Calgary
              </LinkText>
              , , cater to tourists, business travelers, and those in
              transition. Options range from furnished apartments to entire
              homes, providing flexibility and comfort. The area’s proximity to
              parks and attractions enhances its appeal for temporary stays.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.crescent-heights-calgary:Cheap rentals Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in {{ neighborhoodNameWithCity }}, there
              are neighbourhoods where rent is considerably cheaper, so
              exploring these options is a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.crescent-heights-calgary:Exploring Paragraph">
          Crescent Heights, Calgary, Alberta, is a neighborhood that seamlessly
          blends historic charm with modern urban living. Perched above the{' '}
          <LinkText href="https://en.wikipedia.org/wiki/Bow_River">
            Bow River
          </LinkText>
          , it offers unparalleled views of downtown Calgary and the{' '}
          <LinkText href="https://www.travelalberta.com/places-to-go/albertas-regions/canadian-rockies">
            Rocky Mountains
          </LinkText>
          . The community is home to several parks, including{' '}
          <LinkText href="https://www.calgary.ca/parks/rotary-park.html">
            Rotary Park
          </LinkText>{' '}
          and{' '}
          <LinkText href="https://www.calgary.ca/parks/crescent-park.html">
            Crescent Heights Park
          </LinkText>
          , providing residents with ample green spaces for recreation. Its
          central location ensures easy access to downtown amenities, while its
          diverse architecture and vibrant atmosphere make it a unique and
          desirable place to live.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.crescent-heights-calgary:Things To Do Paragraph">
          Crescent Heights offers a variety of activities catering to diverse
          interests. Nature enthusiasts can explore{' '}
          <LinkText href="https://www.calgary.ca/parks/rotary-park.html">
            Rotary Park
          </LinkText>
          , featuring a playground, tennis courts, and a dog park, all with
          stunning city views. The{' '}
          <LinkText href="https://www.calgary.ca/bike-walk-roll/pathways.html">
            Bow River Pathway
          </LinkText>{' '}
          provides scenic routes for walking, jogging, or cycling. Food lovers
          can indulge in the neighborhood’s diverse culinary scene, with
          restaurants offering cuisines ranging from Italian to Vietnamese. Art
          and culture enthusiasts can visit local galleries and participate in
          community events organized by the{' '}
          <LinkText href="https://crescentheightsyyc.ca/">
            Crescent Heights Community Association
          </LinkText>
          . The neighborhood’s blend of natural beauty and urban amenities
          ensures there’s something for everyone.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.crescent-heights-calgary:Restaurants Paragraph">
        <Paragraph>
          Crescent Heights Calgary offers a range of dining options for every
          taste. Here are three restaurants worth trying.
        </Paragraph>
        <Paragraph>
          1) <LinkText href="https://yycagni.ca/">AGNI Fine Cuisine</LinkText> -
          AGNI Fine Cuisine is renowned for its contemporary Indian dishes that
          blend traditional flavors with modern presentation. Signature
          offerings include butter chicken and lamb rogan josh. The elegant
          ambiance makes it ideal for both casual dining and special occasions.
        </Paragraph>
        <Paragraph>
          2){' '}
          <LinkText href="https://santorinirestaurant.com/">
            Santorini Greek Taverna
          </LinkText>{' '}
          - A neighborhood landmark, Santorini offers classic Greek dishes like
          souvlaki and moussaka. The warm, welcoming environment makes it ideal
          for family dinners and group celebrations.
        </Paragraph>
        <Paragraph>
          3){' '}
          <LinkText href="https://www.opentable.ca/r/bar-gigi-calgary">
            Bar Gigi
          </LinkText>{' '}
          - Bar Gigi offers a cozy atmosphere with a menu inspired by Italian
          cuisine. Popular dishes include handcrafted pastas and gourmet pizzas.
          The intimate setting and curated wine list make it a favorite for date
          nights and gatherings.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.crescent-heights-calgary:Transportation Paragraph">
          Getting around {{ neighborhoodNameWithCity }}, AB, is convenient with
          several transportation options. Calgary Transit operates a network of
          buses and the{' '}
          <LinkText href="https://www.calgarytransit.com/home.html">
            CTrain
          </LinkText>
          , a light rail system with two major lines—the Red Line and the Blue
          Line—connecting key areas of the city. For faster bus service, the{' '}
          <LinkText href="https://www.calgarytransit.com/rider-information/max.html">
            MAX Bus Rapid Transit
          </LinkText>{' '}
          (BRT) routes provide efficient connections. Calgary is also home to an
          extensive network of pathways and bikeways, making cycling a viable
          option. While some areas are walkable, the city is largely designed
          for driving, with major roads and highways like the Trans-Canada
          Highway and Deerfoot Trail providing easy access to different parts of
          Calgary. Whether by transit, bike, or car, {{ neighborhoodName }}{' '}
          offers accessible ways to explore the city.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.crescent-heights-calgary:Where To Stay Paragraph">
          Accommodation options in {{ neighborhoodNameWithCity }} cater to those
          seeking a more local and immersive experience. This area features a
          mix of boutique guesthouses, bed-and-breakfasts, and{' '}
          <LinkText href={links.shortTerm}>short-term rentals</LinkText>{' '}
          available through platforms like{' '}
          <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText>, offering
          travelers a unique and personalized stay. While large hotels might be
          limited within {{ neighborhoodName }} itself, its proximity to other
          parts of Calgary ensures that a wide range of traditional
          accommodations, from luxury to budget-friendly, are just a short
          distance away. Staying in {{ neighborhoodName }} gives visitors a
          chance to experience the area’s distinct character while remaining
          conveniently close to the city’s main attractions.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default CrescentHeights
